<!--
  THE DETAILS STRIP — name, meta, two lines of blurb for whatever tile holds
  the focus. One implementation for every surface (the owner, 2026-08-17:
  "every tile, whether it's on a rail or not"): Rail mounts it under the row,
  TileGrid mounts it under the focused tile's ROW of a wrapping grid, and both
  get the same fluid type, the same one-clock reveal, the same TV manners.

  IT READS THE SPOTLIGHT STORE, NOT PROPS. Poster publishes what it is while it
  is the SETTLED focused one (`lightUp`, keyed on `chosen`), so the strip's
  CONTENT needs no plumbing; the only decision a container makes is WHETHER ITS
  OWN strip is open, and it makes it in CSS: any ancestor carrying the class
  `strip-host` opens the strip the moment a `.poster.chosen` is inside it —
  the settled focus, not the live one, so a fling holds the strip steady and
  hands it over once at the settle (see `settledFocus`, focus.svelte.ts).
  `:has()` scoped through `:global` on purpose — Svelte strips a scoped
  `:has(.other-component-child)` silently (docs/SESSION-HANDOFF.md sharp edge
  #1), and one chosen poster in the app means at most one host matches, so the
  one-focus rule holds.

  It is always in the DOM and collapsed to nothing, so revealing it is a
  transition rather than a layout insertion — inserting an element would shove
  every row below down by its height in one frame.
-->
<script lang="ts">
  import AwardMark from './AwardMark.svelte';
  import { awardsFor } from './awards';
  import { spotlight } from './spotlight.svelte';

  interface Props {
    /**
     * Carry the page's own inline padding. A Rail bleeds its track to the
     * screen edge, so its strip pays `--page-pad` itself; a grid sits inside
     * an already-padded wrapper and passes false.
     */
    inset?: boolean;
  }
  let { inset = false }: Props = $props();

  /**
   * READABLE TO A SCREEN READER, WITHOUT A LIVE REGION. The strip used to be
   * aria-hidden, so AT never heard the one paragraph the app writes about the
   * focused tile. A live region would announce every focus hop — chatty, and
   * it repeats the name the tile's own label already says. Instead the meta
   * line and the blurb carry stable per-instance ids, and the CHOSEN poster
   * points its link's `aria-describedby` at them (Poster.svelte finds them
   * through its `strip-host`): the description is read exactly when focus
   * lands, after the tile's own label, and never twice.
   */
  const instance = $props.id();
  const stripBase = `strip-${instance}`;

  /*
   * THE TITLE'S FULL PRIZE RECORD LIVES HERE (the owner, 2026-08-17: "every
   * tile in every rail on any page, when we are focused on it, in the
   * description next to the year or the rating — the tags with ALL the
   * awards"). Best first, compact emblem-and-code marks at the meta line's own
   * scale; each one's precise category is its tooltip and aria-label
   * (AwardMark's `label`). The tile's corner never says this much — only an
   * award rail tags its corners, and only with its own prize.
   */
  let element = $state<HTMLDivElement | null>(null);
  const owns = $derived(spotlight.host !== null && element?.closest('.strip-host') === spotlight.host);
  const prizes = $derived(awardsFor(spotlight.item?.id));
</script>

<div bind:this={element} class="spotlight" class:inset data-strip-base={stripBase} aria-hidden={!owns}>
  <div class="inner">
    {#if spotlight.item}
      <p class="st-title">{spotlight.item.name}</p>
      <p class="st-meta" id="{stripBase}-meta">
        {#if spotlight.item.year}<span>{spotlight.item.year}</span>{/if}
        {#if spotlight.item.imdbRating}<span class="dot">·</span><span>★ {spotlight.item.imdbRating}</span>{/if}
        {#if prizes.length}<span class="dot">·</span><span class="st-awards">
          {#each prizes as a (a.id)}<AwardMark award={a} size={20} />{/each}
        </span>{/if}
        {#if spotlight.item.genres?.length}<span class="dot">·</span><span>{spotlight.item.genres.slice(0, 3).join(' · ')}</span>{/if}
      </p>
      {#if spotlight.item.description}
        <p class="st-blurb" id="{stripBase}-blurb">{spotlight.item.description}</p>
      {/if}
    {/if}
  </div>
</div>

<style>
  /*
   * ── THE REVEAL ───────────────────────────────────────────────────────────
   * Collapsed to zero and animated open with `grid-template-rows: 0fr -> 1fr`,
   * which is the one way to transition to intrinsic height without measuring
   * it in JavaScript or picking a max-height that clips a long blurb.
   *
   * 240ms, the same clock and curve the tile expansion runs on, and the same
   * both ways — moving focus between two hosts closes one strip while the
   * other opens, and two strips on one clock read as a handover where two
   * clocks read as two unrelated lurches. The fade is the 130ms of the focus
   * acknowledgement; everything in this dance is var(--ease-out).
   */
  .spotlight {
    display: grid;
    grid-template-rows: 0fr;
    transition:
      grid-template-rows 240ms var(--ease-out),
      opacity 130ms var(--ease-out);
    opacity: 0;
  }
  .spotlight.inset {
    padding-inline: var(--page-pad);
  }
  .spotlight > .inner {
    overflow: hidden;
    min-height: 0;
  }
  /*
   * THE OPENER. The container declares itself with `strip-host`; the chosen
   * poster inside it is what opens the strip. Global on purpose — see the
   * header — and keyed on the poster's `.chosen`, the SETTLED focus answer
   * (`settledFocus` in focus.svelte.ts), not the live `.focused` one. The
   * reveal is a grid-template-rows animation — layout, every frame, for the
   * whole page below — and keyed on the live answer a fling down Home ran it
   * twice per rail passed, which kept layout dirty through the entire fling
   * and made every scroll-path read a forced reflow (the owner's choppy
   * scroll; docs/PERF-SCROLL.md). Keyed on the settled answer it runs once,
   * at the settle, when the reader can actually read what it opens.
   */
  :global(.strip-host:has(.poster.chosen)) .spotlight {
    grid-template-rows: 1fr;
    opacity: 1;
  }
  /*
   * A TELEVISION'S STRIP SNAPS. The page scroll on a remote press is already
   * instant (`dpad.ts`: a stick's SoC cannot paint an easing AND take the next
   * press), so a strip that goes on easing for 240ms is the one thing still
   * sliding after the frame has settled — every rail-to-rail hop drifted the
   * landed tile by the strip's height and read as the page missing its mark.
   * One frame, one settled layout; the 130ms opacity fade stays, because a
   * cross-fade costs no layout and keeps the handover readable.
   */
  :global(html[data-tv='on']) .spotlight {
    transition: opacity 130ms var(--ease-out);
  }

  /*
   * THE STRIP'S TYPE SCALES WITH THE GLASS (the owner, 2026-08-17: "hard to
   * read, especially on larger devices — make it dynamic"). Fluid rather than
   * stepped: one clamp() per rôle, growing continuously with the viewport.
   *
   *   title  clamp(17px, 14.4px + 0.65vw, 22px) → 17 phone · ~20 iPad · 22 desktop
   *   blurb/meta clamp(13.5px, 12px + 0.4vw, 16px) → 13.5 · ~15.3 · 16
   *
   * (Before: title 15px and blurb/meta 12px everywhere.) The TV override below
   * is separate and biggest of all, because a television is 1280 CSS px read
   * from three metres — the width says "desktop", the distance says "poster".
   * The 0fr→1fr reveal measures intrinsic height, so bigger type just means a
   * taller strip; nothing here is a fixed height that could clip.
   */
  .st-title {
    margin: var(--space-2) 0 2px;
    font-size: clamp(1.0625rem, 0.9rem + 0.65vw, 1.375rem);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
    color: var(--text);
  }
  .st-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.4em;
    margin: 0 0 3px;
    font-size: clamp(0.84375rem, 0.75rem + 0.4vw, 1rem);
    color: var(--text-dim);
  }
  .st-meta .dot {
    color: var(--text-faint);
  }
  /*
   * The award tags, as one run in the meta line. `align-self: center` rather
   * than the row's baseline: a badge's "baseline" is whatever its emblem box
   * reports, and centring the run against the meta text is what actually looks
   * level. At size 20 a badge's box (~17px) sits INSIDE the meta line's own
   * box (~20px at the clamp's floor), so the tags add zero height to the line
   * — the strip's 0fr→1fr reveal measures the same strip with or without them.
   * `pointer-events` back on: the corner tag suppresses it (it rides a link),
   * but here the whole point of hovering a tag is its precise category.
   */
  .st-awards {
    display: inline-flex;
    align-items: center;
    align-self: center;
    gap: 4px;
  }
  .st-awards :global(.award) {
    pointer-events: auto;
  }
  .st-blurb {
    /* Two lines, and a measure a glance can take in. */
    max-width: 62ch;
    margin: 0 0 var(--space-2);
    font-size: clamp(0.84375rem, 0.75rem + 0.4vw, 1rem);
    line-height: var(--leading-snug);
    color: var(--text-dim);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  /*
   * TEN-FOOT TYPE. app.css's TV tier only raises the small tokens (11→12 etc.)
   * — right globally, not enough here: this strip is the one paragraph the app
   * asks a viewer to READ from the sofa. 26/18px CSS is 39/27 real pixels on a
   * 1080p panel — comfortably over the 10-foot floor the token tier aims at.
   */
  :global(html[data-tv='on']) .st-title {
    font-size: 1.625rem;
  }
  :global(html[data-tv='on']) .st-meta,
  :global(html[data-tv='on']) .st-blurb {
    font-size: 1.125rem;
  }

  /*
   * Reduced motion keeps the INFORMATION and drops the movement: the strip
   * still appears, instantly.
   */
  @media (prefers-reduced-motion: reduce) {
    .spotlight {
      transition: none;
    }
  }
</style>
